import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, CartUpdateEvent, CartErrorEvent } from '@theme/events';

class RecipientForm extends Component {
  constructor() {
    super();
    /** @type {?HTMLElement} */
    this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);

    /** @type {?HTMLInputElement} */
    this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
    this.checkboxInput && (this.checkboxInput.disabled = false);

    /** @type {?HTMLInputElement} */
    this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
    this.hiddenControlField && (this.hiddenControlField.disabled = true);

    /** @type {?HTMLInputElement} */
    this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
    /** @type {?HTMLInputElement} */
    this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
    /** @type {?HTMLInputElement} */
    this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
    /** @type {?HTMLInputElement} */
    this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
    /** @type {?HTMLInputElement} */
    this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
    if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

    /** @type {?HTMLElement} */
    this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
    /** @type {HTMLUListElement | null} */
    this.errorMessageList = this.errorMessageWrapper?.querySelector('ul') ?? null;
    /** @type {HTMLElement | null} */
    this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message') ?? null;

    /** @type {string | null} */
    this.defaultErrorHeader = this.errorMessage?.innerText ?? null;

    /** @type {string | null} */
    this.currentProductId = this.dataset.productId ?? null;

    this.addEventListener('change', this.onChange.bind(this));
    this.onChange();
  }

  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest(`.shopify-section, dialog`);

    /**
     * Handles the cart update event.
     * @param {CartUpdateEvent} event - The cart update event.
     */
    const handleCartUpdate = (event) => {
      const productId = event.detail.data?.productId ? event.detail.data.productId.toString() : null;
      if (event.detail.data?.source === 'product-form-component' && productId === this.currentProductId) {
        this.resetRecipientForm();
      }
    };

    /**
     * Handles the cart error event.
     * @param {CartErrorEvent} event - The cart update event.
     */
    const handleCartError = (event) => {
      this.displayErrorMessage(event.detail.data?.message);
    };

    closestSection?.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
    closestSection?.addEventListener(ThemeEvents.cartError, handleCartError);

    this.disconnectedCallback = () => {
      closestSection?.removeEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
      closestSection?.removeEventListener(ThemeEvents.cartError, handleCartError);
    };
  }

  onChange() {
    if (this.checkboxInput?.checked) {
      this.enableInputFields();
      if (this.recipientFieldsLiveRegion) {
        this.recipientFieldsLiveRegion.innerText = Theme.accessibilityStrings.recipientFormExpanded;
      }
    } else {
      this.clearInputFields();
      this.disableInputFields();
      this.clearErrorMessage();
      if (this.recipientFieldsLiveRegion) {
        this.recipientFieldsLiveRegion.innerText = Theme.accessibilityStrings.recipientFormCollapsed;
      }
    }
  }

  inputFields() {
    return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput].filter(Boolean);
  }

  disableableFields() {
    return [...this.inputFields(), this.offsetProperty].filter(Boolean);
  }

  clearInputFields() {
    this.inputFields()
      .filter((field) => field !== null)
      .forEach((field) => (field.value = ''));
  }

  enableInputFields() {
    this.disableableFields()
      .filter((field) => field !== null)
      .forEach((field) => (field.disabled = false));
  }

  disableInputFields() {
    this.disableableFields()
      .filter((field) => field !== null)
      .forEach((field) => (field.disabled = true));
  }

  /**
   * @param {string | {[key: string]: string[]}} body
   */
  displayErrorMessage(body) {
    this.clearErrorMessage();
    if (this.errorMessageWrapper) this.errorMessageWrapper.hidden = false;

    if (typeof body === 'object') {
      if (this.errorMessage) this.errorMessage.innerText = this.defaultErrorHeader || '';

      Object.entries(body).forEach(([key, value]) => {
        const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
        const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
        const message = `${value.join(', ')}`;
        const errorMessageElement = this.querySelector(`#${errorMessageId}`);
        const errorTextElement = errorMessageElement?.querySelector('.error-message');
        if (!(errorTextElement && errorTextElement instanceof HTMLElement)) return;

        if (this.errorMessageList) {
          this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
        }

        errorTextElement.innerText = `${message}.`;
        errorMessageElement?.classList.remove('hidden');

        // @ts-ignore
        const inputElement = this[`${key}Input`];
        if (!inputElement) return;

        inputElement.setAttribute('aria-invalid', 'true');
        inputElement.setAttribute('aria-describedby', errorMessageId);
      });
    } else {
      if (this.errorMessage) this.errorMessage.innerText = body;
    }
  }

  /**
   * @param {string} target
   * @param {string} message
   * @returns {HTMLLIElement}
   */
  createErrorListItem(target, message) {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.setAttribute('href', target);
    a.innerText = message;
    li.appendChild(a);
    li.className = 'error-message';
    return li;
  }

  clearErrorMessage() {
    if (this.errorMessageWrapper) this.errorMessageWrapper.hidden = true;
    if (this.errorMessageList) this.errorMessageList.innerHTML = '';

    this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
      field.classList.add('hidden');
      const textField = field.querySelector('.error-message');
      if (textField instanceof HTMLElement) textField.innerText = '';
    });

    [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
      if (!inputElement) return;
      inputElement.setAttribute('aria-invalid', 'false');
      inputElement.removeAttribute('aria-describedby');
    });
  }

  resetRecipientForm() {
    if (this.checkboxInput?.checked) {
      this.checkboxInput.checked = false;
      this.clearInputFields();
      this.clearErrorMessage();
    }
  }
}

if (!customElements.get('recipient-form')) {
  customElements.define('recipient-form', RecipientForm);
}
